<template>
  <div class="transliterate-input-wrapper">
    <div class="label-row">
      <label v-if="label" class="input-label" :for="inputId">
        {{ label }}
        <span v-if="required" class="required-mark">*</span>
      </label>

      <!-- Language Toggle -->
      <div class="language-toggle">
        <button
          type="button"
          class="toggle-btn"
          :class="{ 'is-active': mode === 'english' }"
          @click="setMode('english')"
        >
          English
        </button>
        <button
          type="button"
          class="toggle-btn"
          :class="{ 'is-active': mode === 'tamil' }"
          @click="setMode('tamil')"
        >
          தமிழ்
        </button>
      </div>
    </div>

    <div class="input-container" :class="{ 'is-focused': isFocused, 'has-error': error }">
      <input
        :id="inputId"
        ref="inputRef"
        v-model="inputText"
        type="text"
        class="transliterate-input"
        :placeholder="currentPlaceholder"
        :disabled="disabled"
        :required="required"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown="handleKeydown"
      />

      <!-- Tamil preview badge (only in Tamil mode) -->
      <div v-if="mode === 'tamil' && tamilPreview && inputText && tamilPreview !== inputText" class="tamil-preview">
        {{ tamilPreview }}
      </div>

      <!-- Loading indicator -->
      <div v-if="isLoading && mode === 'tamil'" class="loading-indicator">
        <span class="spinner"></span>
      </div>
    </div>

    <!-- Suggestions dropdown (only in Tamil mode) -->
    <div
      v-if="mode === 'tamil' && showSuggestions && suggestions.length > 0"
      class="suggestions-dropdown"
      ref="dropdownRef"
    >
      <button
        v-for="(suggestion, index) in suggestions"
        :key="index"
        type="button"
        class="suggestion-item"
        :class="{ 'is-selected': selectedIndex === index }"
        @click="selectSuggestion(suggestion)"
        @mouseenter="selectedIndex = index"
      >
        <span class="suggestion-tamil">{{ suggestion.tamil }}</span>
        <span class="suggestion-confidence">{{ Math.round(suggestion.confidence * 100) }}%</span>
      </button>
    </div>

    <!-- Hint text -->
    <p v-if="currentHint && !error" class="hint-text">{{ currentHint }}</p>

    <!-- Error message -->
    <p v-if="error" class="error-text">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useTransliterate } from '../../composables/useTransliterate.js'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  placeholderTamil: {
    type: String,
    default: 'Type in English for Tamil...'
  },
  placeholderEnglish: {
    type: String,
    default: 'Type name in English...'
  },
  hint: {
    type: String,
    default: ''
  },
  hintTamil: {
    type: String,
    default: 'Type in English, we\'ll convert to Tamil'
  },
  hintEnglish: {
    type: String,
    default: ''
  },
  error: {
    type: String,
    default: ''
  },
  required: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  debounceDelay: {
    type: Number,
    default: 150
  },
  defaultMode: {
    type: String,
    default: 'english',
    validator: (v) => ['tamil', 'english'].includes(v)
  }
})

const emit = defineEmits(['update:modelValue', 'transliterated', 'modeChange'])

// Composable
const {
  isLoading,
  suggestions: transliterateSuggestions,
  transliterateDebounced,
  cancelDebounce
} = useTransliterate()

// State
const inputRef = ref(null)
const dropdownRef = ref(null)
const inputText = ref('')
const tamilPreview = ref('')
const isFocused = ref(false)
const showSuggestions = ref(false)
const selectedIndex = ref(0)
const mode = ref(props.defaultMode)

// Generate unique ID for label association
const inputId = computed(() => `transliterate-${Math.random().toString(36).substr(2, 9)}`)

// Map suggestions from composable
const suggestions = computed(() => transliterateSuggestions.value || [])

// Dynamic placeholder based on mode
const currentPlaceholder = computed(() => {
  if (props.placeholder) return props.placeholder
  return mode.value === 'tamil' ? props.placeholderTamil : props.placeholderEnglish
})

// Dynamic hint based on mode
const currentHint = computed(() => {
  if (props.hint) return props.hint
  return mode.value === 'tamil' ? props.hintTamil : props.hintEnglish
})

// Watch for external model value changes (e.g., editing existing data)
watch(() => props.modelValue, (newVal) => {
  if (newVal && !inputText.value) {
    // Detect if it's Tamil text
    const isTamil = /[\u0B80-\u0BFF]/.test(newVal)
    if (isTamil) {
      mode.value = 'tamil'
      tamilPreview.value = newVal
      // Don't set inputText - user needs to type to edit
    } else {
      mode.value = 'english'
      inputText.value = newVal
    }
  }
}, { immediate: true })

// Set mode
const setMode = (newMode) => {
  if (mode.value === newMode) return

  const oldMode = mode.value
  mode.value = newMode

  // Clear suggestions when switching modes
  showSuggestions.value = false
  tamilPreview.value = ''

  // Re-emit the current value in new mode
  if (inputText.value) {
    if (newMode === 'english') {
      // Switch to English: use the typed text as-is
      emit('update:modelValue', inputText.value)
    } else {
      // Switch to Tamil: trigger transliteration
      handleInput()
    }
  }

  emit('modeChange', newMode)
}

// Handle input changes
const handleInput = async () => {
  const text = inputText.value.trim()

  if (!text) {
    tamilPreview.value = ''
    showSuggestions.value = false
    emit('update:modelValue', '')
    return
  }

  if (mode.value === 'english') {
    // English mode: just emit the text as-is
    emit('update:modelValue', text)
    return
  }

  // Tamil mode: transliterate with debouncing
  const result = await transliterateDebounced(text, props.debounceDelay)

  if (result && result.suggestions && result.suggestions.length > 0) {
    tamilPreview.value = result.text || result.suggestions[0].tamil
    showSuggestions.value = true
    selectedIndex.value = 0

    // Auto-emit the best suggestion
    emit('update:modelValue', tamilPreview.value)
    emit('transliterated', {
      english: text,
      tamil: tamilPreview.value,
      suggestions: result.suggestions
    })
  } else {
    tamilPreview.value = text
    showSuggestions.value = false
    emit('update:modelValue', text)
  }
}

// Handle focus
const handleFocus = () => {
  isFocused.value = true
  if (mode.value === 'tamil' && suggestions.value.length > 0 && inputText.value) {
    showSuggestions.value = true
  }
}

// Handle blur with delay to allow click on suggestions
const handleBlur = () => {
  setTimeout(() => {
    isFocused.value = false
    showSuggestions.value = false
  }, 200)
}

// Handle keyboard navigation
const handleKeydown = (event) => {
  if (mode.value === 'english') return
  if (!showSuggestions.value || suggestions.value.length === 0) return

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      selectedIndex.value = Math.min(selectedIndex.value + 1, suggestions.value.length - 1)
      break
    case 'ArrowUp':
      event.preventDefault()
      selectedIndex.value = Math.max(selectedIndex.value - 1, 0)
      break
    case 'Enter':
      event.preventDefault()
      if (suggestions.value[selectedIndex.value]) {
        selectSuggestion(suggestions.value[selectedIndex.value])
      }
      break
    case 'Escape':
      showSuggestions.value = false
      break
    case 'Tab':
      // Accept current suggestion on Tab
      if (suggestions.value[selectedIndex.value]) {
        selectSuggestion(suggestions.value[selectedIndex.value])
      }
      break
  }
}

// Select a suggestion
const selectSuggestion = (suggestion) => {
  tamilPreview.value = suggestion.tamil
  showSuggestions.value = false
  emit('update:modelValue', suggestion.tamil)
  emit('transliterated', {
    english: inputText.value,
    tamil: suggestion.tamil,
    suggestions: suggestions.value
  })

  // Keep focus on input for continued typing
  nextTick(() => {
    inputRef.value?.focus()
  })
}

// Click outside to close dropdown
const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target) &&
      inputRef.value && !inputRef.value.contains(event.target)) {
    showSuggestions.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  cancelDebounce()
})
</script>

<style scoped>
@import '../../styles/intercom-tokens.css';

.transliterate-input-wrapper {
  position: relative;
  width: 100%;
}

.label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-1);
  gap: var(--space-2);
}

.input-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.required-mark {
  color: var(--color-error);
  margin-left: 2px;
}

/* Language Toggle */
.language-toggle {
  display: flex;
  background: var(--color-gray-100);
  border-radius: var(--radius-sm);
  padding: 2px;
}

.toggle-btn {
  padding: var(--space-1) var(--space-2);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-500);
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all 0.15s ease;
}

.toggle-btn:hover:not(.is-active) {
  color: var(--color-charcoal);
}

.toggle-btn.is-active {
  background: var(--color-white);
  color: var(--color-charcoal);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.input-container {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--color-white);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  transition: all 0.15s ease;
}

.input-container.is-focused {
  border-color: var(--color-blue);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.input-container.has-error {
  border-color: var(--color-error);
}

.transliterate-input {
  flex: 1;
  padding: var(--space-2) var(--space-3);
  border: none;
  background: transparent;
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  outline: none;
  min-width: 0;
}

.transliterate-input::placeholder {
  color: var(--color-gray-400);
}

.transliterate-input:disabled {
  background: var(--color-gray-50);
  cursor: not-allowed;
}

.tamil-preview {
  padding: var(--space-1) var(--space-2);
  margin-right: var(--space-2);
  background: var(--color-blue-50, #eff6ff);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-sm);
  color: var(--color-blue);
  white-space: nowrap;
}

.loading-indicator {
  padding-right: var(--space-3);
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid var(--color-gray-200);
  border-top-color: var(--color-blue);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Suggestions dropdown */
.suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: var(--space-1);
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  z-index: 100;
  max-height: 200px;
  overflow-y: auto;
}

.suggestion-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background 0.1s;
}

.suggestion-item:hover,
.suggestion-item.is-selected {
  background: var(--color-gray-50);
}

.suggestion-tamil {
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
}

.suggestion-confidence {
  font-size: var(--font-size-xs);
  color: var(--color-gray-400);
  margin-left: var(--space-2);
}

/* Hint and error text */
.hint-text {
  margin-top: var(--space-1);
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
}

.error-text {
  margin-top: var(--space-1);
  font-size: var(--font-size-xs);
  color: var(--color-error);
}
</style>
